<script setup>
import {useDjangoForm} from "@/utils/forms";
import * as yup from "yup";
import {oneOfNumberValidator, requiredMessage, stringValidator} from "@/utils/yupValidators";
import {HpoRole, HpoRoleLabel} from "@/components/hpo/choices";
import {computed, watch} from "vue";
import {useMutation} from "@vue/apollo-composable";
import {HpoCreateMemberTypePreviewMutation} from "@/graphql/mutations/hpo/memberTypePreview.graphql";
import Modal from "@/components/modal/Modal.vue";
import {useRouter} from "vue-router";
import {mdiOpenInNew} from "@mdi/js";

const props = defineProps({
    hpoId: {
        type: String,
        required: true
    },
    memberTypeOptions: {
        type: Array,
        required: true
    },
    hospitalList: {
        type: Array,
        required: false,
        default: []
    },
});
const emit = defineEmits(['close']);

const router = useRouter();

const {defineField, handleMutationSubmit, nonFieldErrors} = useDjangoForm({
    validationSchema: yup.object({
        role: oneOfNumberValidator(HpoRole),
        hpoHospitals: yup.array().when(
            'role', {
                is: (val) => parseInt(val) === HpoRole.CONTACT_POINT,
                then: () => {
                    return yup.array().of(yup.string().oneOf(Object.keys(hospitalOptions.value)))
                        .min(1, gettext('At least one hospital must be selected'))
                        .max(Object.values(hospitalOptions.value).length)
                },
                otherwise: () => {
                    return yup.array().of(yup.number().integer()).max(0, gettext('No hospital can be selected'))
                }
            }
        ),
    }),
    initialValues: {
        role: props.memberTypeOptions[0],
        hpoHospitals: [],
    }
});
const [role, roleProps] = defineField('role');
const [hpoHospitals, hpoHospitalsProps] = defineField('hpoHospitals');

watch(role, async (newVal, oldVal) => {
    if (parseInt(newVal) !== HpoRole.CONTACT_POINT) {
        hpoHospitals.value = [];
    }
});

const {mutate} = useMutation(HpoCreateMemberTypePreviewMutation);

const onSubmit = handleMutationSubmit(
    (values) => {
        return mutate({...values, hpo: props.hpoId})
    },
    {
        onSuccess(result) {
            const memberId = result.hpoMember.id;
            const previewLocation = router.resolve({to: 'hpo:dashboard', params: {'id': props.hpoId}, query: {memberId}});
            window.open(previewLocation.href, '_blank');
        },
    }
);

function close() {
    emit('close');
}

const hospitalOptions = computed(() => {
    return props.hospitalList.reduce((obj, item) => Object.assign(obj, {[item.id]: item.name}), {});
});

const typeOptions = computed(() => {
    return props.memberTypeOptions
        .map(type => ({value: type, title: HpoRoleLabel[type]}))
        .sort((a, b) => a.title.localeCompare(b.title, 'cs'));
});
</script>

<template>
    <Modal :title="pgettext('title', 'Preview as role')" :max-width="960" @close="close" :closeOnClickOutside="false">
        <v-form @submit.prevent="onSubmit">
            <v-row>
                <v-col cols="12">
                    <v-alert v-for="message in nonFieldErrors" :title="message" type="error"></v-alert>

                    <v-select v-model="role"
                              v-bind="roleProps"
                              :label="pgettext('label', 'Type')"
                              :items="typeOptions"/>


                    <template v-if="parseInt(role) === HpoRole.CONTACT_POINT">
                        <v-checkbox
                            v-for="([value, label], index) in Object.entries(hospitalOptions)"
                            :key="index"
                            v-model="hpoHospitals"
                            v-bind="hpoHospitalsProps"
                            :label="label"
                            :value="Number(value)"
                            color="primary"
                            density="compact"/>
                    </template>
                    <v-btn :prepend-icon="mdiOpenInNew" block type="submit">
                        {{ pgettext('action', 'Open preview') }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
    </Modal>
</template>
